import React, { useState } from "react";
import Radio from "antd/lib/radio";
import Form from "antd/lib/form";
import moment from "moment";
import { AppDatePicker } from "components/inputs";
import { SurveyNumberInput } from "pages/symptoms/surveyForms/surveyNumberInput";
import { disableFutureDate } from "helpers/utils";
import {
  SurveyQuestionCard,
  AnswerList,
  AnswerOption,
  FormItemStyle,
  AsteriskStyle,
  CardTitle
} from "pages/symptoms/surveyForms/surveyFormComponents";

interface WeightSectionProps {
  readonly currentPage: number;
  readonly weightScale: string;
  setShowSubmit(show: boolean): void;
}

export const WeightSection: React.FC<WeightSectionProps> = ({
  currentPage,
  weightScale,
  setShowSubmit
}) => {
  const [weight, setWeight] = useState(localStorage.getItem(`weight`) || "");

  const checkWeight = (_: any, value: number) => {
    if (value) {
      if (value <= 600 && value >= 30) {
        return Promise.resolve();
      }
      return Promise.reject(
        new Error(
          "Reading appears out of range; Please recheck and reenter value between 30 - 600."
        )
      );
    }
    return Promise.reject(new Error("This field is required"));
  };

  const scaleUnit = weightScale ? weightScale.split(/\s+/).splice(-1)[0] : "";

  return (
    <>
      {currentPage === 10 && (
        <SurveyQuestionCard
          sectionTag="Weight"
          title="Do you want to report your weight?"
          required
        >
          <FormItemStyle
            name="Report your weight"
            label=""
            rules={[{ required: true }]}
            initialValue={""}
          >
            <Radio.Group
              value={localStorage.getItem("reportYourWeight")}
              onChange={e => {
                if (e.target.value === "No") {
                  setShowSubmit(true);
                } else {
                  setShowSubmit(false);
                }
                localStorage.setItem("reportYourWeight", e.target.value);
              }}
            >
              <AnswerList>
                <AnswerOption value="Yes">Yes</AnswerOption>
                <AnswerOption value="No">No</AnswerOption>
              </AnswerList>
            </Radio.Group>
          </FormItemStyle>
        </SurveyQuestionCard>
      )}

      {currentPage === 11 && (
        <SurveyQuestionCard
          sectionTag="Weight"
          title="When did you take your weight?"
          required
        >
          <FormItemStyle
            name="When did you take your weight"
            label=""
            rules={[{ required: true }]}
            initialValue={""}
          >
            <Radio.Group
              value={localStorage.getItem("whenWasWeightTaken")}
              onChange={e =>
                localStorage.setItem("whenWasWeightTaken", e.target.value)
              }
            >
              <AnswerList>
                <AnswerOption value="Taken today">Taken today</AnswerOption>
                <AnswerOption value="Taken before today">
                  Taken before today
                </AnswerOption>
              </AnswerList>
            </Radio.Group>
          </FormItemStyle>
        </SurveyQuestionCard>
      )}

      {currentPage === 12 && (
        <SurveyQuestionCard
          sectionTag="Weight"
          title="When was your weight taken?"
          required
        >
          <FormItemStyle
            name="When was your weight taken(Date)"
            label=""
            rules={[{ required: true }]}
            initialValue={""}
          >
            <AppDatePicker
              label="Weight Taken on"
              value={moment(localStorage.getItem("dateYourWeightWasTaken"))}
              onChange={e => {
                if (e) {
                  const selectedDate = moment(e).toISOString();
                  localStorage.setItem("dateYourWeightWasTaken", selectedDate);
                }
              }}
              style={{ width: "250px" }}
              disabledDate={disableFutureDate}
            />
          </FormItemStyle>
        </SurveyQuestionCard>
      )}

      {currentPage === 13 && (
        <SurveyQuestionCard
          sectionTag="Weight"
          title="Please select your scale"
          required
        >
          <FormItemStyle
            name={`Select scale`}
            label=""
            rules={[{ required: true }]}
            initialValue={""}
          >
            <Radio.Group
              value={localStorage.getItem("selectScale")}
              onChange={e =>
                localStorage.setItem("selectScale", e.target.value)
              }
            >
              <AnswerList>
                <AnswerOption value="Report in Pounds">
                  Report in Pounds
                </AnswerOption>
                <AnswerOption value="Report in Kilograms">
                  Report in Kilograms
                </AnswerOption>
              </AnswerList>
            </Radio.Group>
          </FormItemStyle>
        </SurveyQuestionCard>
      )}

      {currentPage === 14 && (
        <SurveyQuestionCard
          sectionTag="Weight"
          title={`Report your weight in ${scaleUnit}`}
          required
        >
          <FormItemStyle
            name={`weight`}
            label=""
            rules={[
              {
                required: true,
                validator: checkWeight,
                validateTrigger: "submit"
              }
            ]}
            initialValue={""}
          >
            <SurveyNumberInput
              value={weight}
              decimalLimit={2}
              placeholder="Your Answer"
              onChange={e => {
                setWeight(e as string);
                localStorage.setItem(`weight`, e as string);
              }}
              min={0}
              style={{ width: "160px" }}
            />
          </FormItemStyle>
        </SurveyQuestionCard>
      )}
    </>
  );
};
